/**
 * Pinia SSR Helpers for Nuxt 2
 *
 * This module provides utilities to make Pinia stores work seamlessly with Nuxt 2 SSR.
 * It ensures that stores have access to Nuxt context ($axios, etc.) during SSR.
 */

// Import all store initializers directly to avoid dynamic imports
import { initializeSearchStore } from '@/stores/useSearchStore'
import { initializeContentStore } from '@/stores/useContentStore'
import { initializeBrandStore } from '@/stores/useBrandStore'
import { initializeCategoryStore } from '@/stores/useCategoryStore'
import { initializeStoreLocatorStore } from '@/stores/useStoreLocatorStore'
import { initializeHomepageStore } from '@/stores/useHomepageStore'
import { initializeStoredCardsStore } from '@/stores/useStoredCardsStore'

/**
 * Helper to use Pinia stores in asyncData/fetch with proper SSR support
 *
 * @param {Object} context - Nuxt context from asyncData/fetch
 * @param {Function} storeGetter - Function that returns the store (e.g., () => useSearchStore())
 * @param {String} storeName - Name of the store for initialization
 * @returns {Object} The initialized store instance
 */
export function usePiniaStore(context, storeGetter, storeName) {
  const { $axios } = context

  // Only initialize on server
  if (process.server && $axios) {
    try {
      // Initialize stores based on name
      switch (storeName) {
        case 'search':
          initializeSearchStore($axios)
          break
        case 'content':
          initializeContentStore($axios, context)
          break
        case 'brand':
          initializeBrandStore($axios, context)
          break
        case 'category':
          initializeCategoryStore($axios, context)
          break
        case 'storeLocator':
          initializeStoreLocatorStore($axios)
          break
        case 'homepage':
          initializeHomepageStore($axios)
          break
        case 'storedCards':
          initializeStoredCardsStore({ $axios, $auth: context.app.$auth })
          break
        case 'root':
          // Root store doesn't need initialization
          break
        default:
          console.warn(`Unknown store name: ${storeName}`)
      }
    } catch (error) {
      console.warn(`Failed to initialize ${storeName} store:`, error)
    }
  }

  // Return the store instance
  return storeGetter()
}

/**
 * Helper to fetch data with Pinia stores in pages/components
 * Handles both asyncData (SSR) and client-side fetching
 *
 * @param {Object} options - Configuration object
 * @param {Function} options.fetchData - Async function to fetch data
 * @param {Array} options.stores - Array of store configurations
 * @param {Function} options.mapState - Function to map store state to component data
 * @returns {Object} Mixin object for Vue component
 */
export function createPiniaFetchMixin(options) {
  const { fetchData, stores = [], mapState } = options

  return {
    async asyncData(context) {
      // Initialize stores for SSR
      const initializedStores = {}

      for (const storeConfig of stores) {
        const { name, useStore } = storeConfig
        initializedStores[name] = usePiniaStore(context, useStore, name)
      }

      // Fetch data with initialized stores
      await fetchData({ ...context, stores: initializedStores })

      // Map state for hydration
      if (mapState) {
        return mapState(initializedStores)
      }

      return {}
    },

    mounted() {
      // Rehydrate stores on client if we have mapped state
      if (mapState && this.$data) {
        const storeInstances = {}

        for (const storeConfig of stores) {
          const { name, useStore } = storeConfig
          storeInstances[name] = useStore()
        }

        // Apply any custom rehydration logic
        if (options.rehydrate) {
          options.rehydrate.call(this, storeInstances)
        }
      }
    },
  }
}

/**
 * Simple wrapper for pages that just need to ensure store initialization
 *
 * @param {Function} useStore - Store getter function
 * @param {String} storeName - Name of the store
 * @returns {Object} Object with asyncData method
 */
export function withPiniaSSR(useStore, storeName) {
  return {
    asyncData(context) {
      usePiniaStore(context, useStore, storeName)
      return {}
    },
  }
}

/**
 * Wrapper for asyncData that automatically handles SSR hydration
 * Use this to wrap your existing asyncData method
 *
 * @param {Function} asyncDataFn - Your original asyncData function
 * @returns {Function} Wrapped asyncData function
 */
export function withSSRHydration(asyncDataFn) {
  return function wrappedAsyncData(context) {
    // Skip on client if we're hydrating from SSR
    if (process.client && context.nuxtState && context.nuxtState.pinia) {
      return {}
    }

    // Call the original asyncData
    return asyncDataFn.call(this, context)
  }
}
